/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logQnaKBButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { SublistSynonymsCell } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistSynonymsCell';
import { QnaMakerFollowUpPromptModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerFollowUpPromptModal';
import { QnaMakerMetadataType, QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaPrompt } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';

type AddQnaPairModalProps = BasicModalProps<object> & {
    selectedSourceId?: string;
    sources: QnaMakerSourceStore[];
    qnaPairsJson: QnaMakerQnaSwagger[];
    onSubmitQnaPair: (newData: Partial<QnaMakerQnaSwagger>) => void;
    onEditQnaPair: (newData: Partial<QnaMakerQnaSwagger>, qnaId: number) => void;
};

const messages = defineMessages({
    AddQnaPair: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.addQnaPair',
        defaultMessage: 'Add a new question answer pair'
    },
    description: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.description',
        defaultMessage: 'Add a new question answer pair by entering a question, answer, and selecting the source.'
    },
    source: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.source',
        defaultMessage: 'Source'
    },
    sourcePlaceholder: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.sourcePlaceholder',
        defaultMessage: 'Select a source'
    },
    question: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.question',
        defaultMessage: 'Question'
    },
    questionPlaceholder: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.questionPlaceholder',
        defaultMessage: 'Enter the question'
    },
    answer: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.answer',
        defaultMessage: 'Answer'
    },
    addFollowUpPrompts: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.addFollowUpPrompts',
        defaultMessage: 'Add follow up prompts (optional)'
    },
    followUpPrompts: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.followUpPrompts',
        defaultMessage: 'Follow up prompt'
    },
    addMetadata: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.addMetadata',
        defaultMessage: 'Add metadata (optional)'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.metadata',
        defaultMessage: 'Metadata'
    },
    key: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.key',
        defaultMessage: 'Key:'
    },
    value: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.value',
        defaultMessage: 'Value:'
    },
    keyPlaceholder: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.keyPlaceholder',
        defaultMessage: 'Add key and press tab'
    },
    valuePlaceholder: {
        id: 'qnaMaker.qnaMakerAddQnaPairModal.valuePlaceholder',
        defaultMessage: 'Add value and press esc'
    }
});

const theme = getScopedTheme('QnaMakerQnaPairDetails');

const addButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    label: { color: theme.buttonColor },
    labelDisabled: { color: theme.disabledLabelColor }
});

/**
 * Represents a modal to add a new qna pair group.
 *
 * @param props The properties passed to the modal.
 */
export const QnaMakerAddQnaPairModal = observer(
    withModalMounter((props: AddQnaPairModalProps) => {
        const { onEditQnaPair, onSubmitQnaPair, sources, selectedSourceId, qnaPairsJson, onDismiss, ...rest } = props;

        const { localize } = useLocalization();
        const { rootStore } = useGlobalState();
        const { serviceName } = getPortalFromUrl();

        const { languageDirection } = rootStore.currentQnaMakerProjectStore;

        const qnaId = getRandomNumber();

        const [prompts, setPrompts] = React.useState<QnaMakerQnaPrompt[]>([]);

        const hiddenQnaPairIds = [...prompts.map(p => p.qnaId), qnaId, ...qnaPairsJson.filter(p => p.internalRef).map(p => p.id)];

        const displayedSources: IDropdownOption[] = sources.map(s => ({ key: s.source, text: s.source }));
        if (!displayedSources.some(src => src.key === 'Editorial')) {
            displayedSources.unshift({ key: 'Editorial', text: 'Editorial' });
        }

        const [selectedSource, setSelectedSource] = React.useState(
            selectedSourceId ? displayedSources.find(s => s.key === selectedSourceId) : displayedSources[0]
        );

        const [question, setQuestion] = React.useState('');

        const [answer, setAnswer] = React.useState('');

        const [isAddMetadataOn, setIsAddMetadataOn] = React.useState(false);

        const [editableKey, setEditableKey] = React.useState('');
        const [editableValue, setEditableValue] = React.useState('');
        const [metadata, setMetadata] = React.useState<QnaMakerMetadataType>({});

        const [isPromptModalHidden, setIsPromptModalHidden] = React.useState(true);
        const [promptToBeEditedId, setPromptToBeEditedId] = React.useState(null);

        const logClick = (prop: string) => logQnaKBButtonClick(serviceName, 'AddQnAModal', 'Button', prop);

        const handleModifyingPrompts = (newPrompts: QnaMakerQnaPrompt[], linkedPair?: Partial<QnaMakerQnaSwagger>) => {
            if (linkedPair) {
                onEditQnaPair({ ...linkedPair }, linkedPair.id);
            }
            setPrompts([...newPrompts]);
        };

        const handleFollowUpPromptSubmit = ({
            prompt,
            linkedPair
        }: {
            prompt: QnaMakerQnaPrompt;
            linkedPair: Partial<QnaMakerQnaSwagger>;
        }) => {
            setIsPromptModalHidden(true);

            if (promptToBeEditedId) {
                setPromptToBeEditedId(null);
                handleModifyingPrompts(
                    prompts.map(p => (p.qnaId === promptToBeEditedId ? prompt : p)),
                    linkedPair
                );

                return;
            }

            const newDisplayedOrder = Math.max(...prompts.map(p => p.displayOrder)) + 1;
            handleModifyingPrompts([...prompts, { ...prompt, displayOrder: newDisplayedOrder }], linkedPair);
        };

        const onSubmitNewMetadata = () => {
            if (editableKey.length > 0 && editableValue.length > 0) {
                setMetadata({ ...metadata, [editableKey]: editableValue });
            }
            setIsAddMetadataOn(false);
            setEditableKey('');
            setEditableValue('');
        };

        const handleSubmitQnaPair = () => {
            logClick('Add');
            onSubmitQnaPair({
                id: qnaId,
                source: String(selectedSource.key),
                questions: [question],
                answer,
                metadata,
                dialog: { isContextOnly: false, prompts }
            });
        };

        const onAddMetadataClick = () => {
            logClick('AddMetadata');
            setIsAddMetadataOn(true);
        };

        const onAddFollowUpPromptClick = () => {
            logClick('AddFollowUpPrompt');
            setIsPromptModalHidden(false);
        };

        const handleDismiss = () => {
            logClick('Cancel');
            onDismiss();
        };

        return (
            <BasicModal
                {...rest}
                onDismiss={handleDismiss}
                onSubmit={handleSubmitQnaPair}
                modalProps={{ isBlocking: true }}
                valid={selectedSource && question.length > 0 && answer.length > 0}
                dialogContentProps={{ title: localize(messages.AddQnaPair), styles: { inner: { overflowY: 'auto' } } }}>
                <Stack verticalFill tokens={{ childrenGap: 20 }}>
                    <LuisText>{localize(messages.description)}</LuisText>
                    <LuisDropdown
                        required
                        options={displayedSources}
                        selectedKey={selectedSource?.key}
                        label={localize(messages.source)}
                        placeholder={localize(messages.sourcePlaceholder)}
                        onChange={(_, option) => setSelectedSource(option)}
                    />
                    <LuisTextField
                        required
                        value={question}
                        label={localize(messages.question)}
                        onChange={(_, newValue) => setQuestion(newValue)}
                    />
                    <LuisTextField
                        required
                        multiline
                        value={answer}
                        resizable={false}
                        label={localize(messages.answer)}
                        styles={{ root: { height: '100px' } }}
                        onChange={(_, newValue) => setAnswer(newValue)}
                    />
                    <Stack tokens={{ childrenGap: 5 }}>
                        <LuisText fontWeight={600}>{localize(messages.addMetadata)}</LuisText>
                        <ActionButton
                            styles={addButtonStyles}
                            disabled={isAddMetadataOn}
                            iconProps={{ iconName: 'Add' }}
                            text={localize(messages.metadata)}
                            onClick={onAddMetadataClick}
                        />
                        <SublistSynonymsCell
                            isEditMode={false}
                            onAddValues={undefined}
                            isRemovingDisabled={false}
                            onExitFocus={() => setIsAddMetadataOn(false)}
                            values={Object.entries(metadata ?? []).map(e => `${e[0]}: ${e[1]}`)}
                            onRemoveValues={values =>
                                setMetadata(
                                    Object.fromEntries(
                                        Object.entries(metadata).filter(([key]) => values.some(v => v.split(':')[0] !== key))
                                    )
                                )
                            }
                        />
                        {isAddMetadataOn && (
                            <Stack horizontal tokens={{ childrenGap: 15 }}>
                                <Stack tokens={{ childrenGap: 5 }}>
                                    <LuisText>{localize(messages.key)}</LuisText>
                                    <LuisTextField
                                        required
                                        autoFocus
                                        value={editableKey}
                                        dir={languageDirection}
                                        onChange={(_, v) => setEditableKey(v)}
                                        placeholder={localize(messages.keyPlaceholder)}
                                    />
                                </Stack>
                                <LuisText styles={{ root: { display: 'flex', paddingTop: '20px' } }}>:</LuisText>
                                <Stack tokens={{ childrenGap: 5 }}>
                                    <LuisText>{localize(messages.value)}</LuisText>
                                    <LuisTextField
                                        required
                                        value={editableValue}
                                        dir={languageDirection}
                                        onBlur={onSubmitNewMetadata}
                                        onChange={(_, v) => setEditableValue(v)}
                                        placeholder={localize(messages.valuePlaceholder)}
                                        onKeyUp={e => {
                                            if (e.key === LuisKeyCodes.Enter) {
                                                e.preventDefault();
                                                onSubmitNewMetadata();
                                            }
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>

                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText fontWeight={600}>{localize(messages.addFollowUpPrompts)}</LuisText>
                    <ActionButton
                        styles={addButtonStyles}
                        disabled={isAddMetadataOn}
                        iconProps={{ iconName: 'Add' }}
                        text={localize(messages.followUpPrompts)}
                        onClick={onAddFollowUpPromptClick}
                    />
                    {prompts.map(p => (
                        <LuisText>{p.displayText}</LuisText>
                    ))}
                </Stack>

                {!isPromptModalHidden && (
                    <QnaMakerFollowUpPromptModal
                        qnaPairsJson={qnaPairsJson}
                        hidden={isPromptModalHidden}
                        isContextOnly={false}
                        selectedQnaId={qnaId}
                        hiddenQnaPairIds={hiddenQnaPairIds}
                        onSubmit={handleFollowUpPromptSubmit}
                        prompt={prompts.find(p => p.qnaId === promptToBeEditedId)}
                        onDismiss={() => {
                            setIsPromptModalHidden(true);
                            setPromptToBeEditedId(null);
                        }}
                    />
                )}
            </BasicModal>
        );
    })
);
